import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
  SidebarGroupLabel,
} from "@/components/ui/sidebar";
import {
  LogOut,
  User,
  Receipt,
  Settings2,
  HardHat,
  Thermometer,
  Lightbulb,
  AudioLines,
  BrainCircuit,
  Layout,
  LogIn,
  ChevronDown,
  Files,
  Archive,
} from "lucide-react";
import { Link, NavLink } from "react-router";
import { useLogout } from "@/hooks/use-logout";
import { useUserDetails } from "@/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLocation } from "react-router";

const reportGenLink = [
  {
    title: "Ruido dosis",
    url: "/ruido-dosis",
    icon: AudioLines,
  },
  {
    title: "Iluminación",
    url: "/iluminacion",
    icon: Lightbulb,
  },
  {
    title: "Temperatura",
    url: "/temperatura",
    icon: Thermometer,
  },
];
const toolLinks = [
  {
    title: "Capturar datos",
    url: "/capturar-datos",
    icon: BrainCircuit,
  },
  {
    title: "Layouts",
    url: "/layouts",
    icon: Layout,
  },
  {
    title: "Unir pdfs",
    url: "/unir-pdfs",
    icon: Files,
  },
  // {
  //   title: "Ayuda",
  //   url: "/ayuda",
  //   icon: CircleHelp,
  // },
];

const personalLinks = [
  {
    title: "Equipos",
    url: "/equipos",
    icon: HardHat,
  },
  {
    title: "Configuración",
    url: "/configuracion",
    icon: Settings2,
  },
  {
    title: "Reportes",
    url: "/reportes",
    icon: Archive,
  }
];

export const Sidebar = ({
  ...props
}: React.ComponentProps<typeof SidebarRoot>) => {
  const { data: user } = useUserDetails();
  const logout = useLogout();
  const location = useLocation();

  return (
    <SidebarRoot {...props} className="font-mono" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="py-2">
            <SidebarMenuButton asChild className="hover:bg-sidebar">
              <Link to="/">
                <img src="/favicon.svg" className="w-[30px]" />{" "}
                <span className="group-data-[collapsible=icon]:hidden font-semibold text-primary text-lg font-sans tracking-tight">
                  BERNARDINO
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold h-4">
            Generar reportes
          </SidebarGroupLabel>
          <SidebarMenu>
            {reportGenLink.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith(item.url)}
                >
                  <NavLink
                    to={item.url}
                    className={({ isActive }) => (isActive ? "bg-sidebar" : "")}
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold h-4">
            Herramientas
          </SidebarGroupLabel>
          <SidebarMenu>
            {toolLinks.map((item) => (
              <SidebarMenuItem key={item.title}>
                {user?.type === "verified" || user?.type === "subscribed" ? (
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith(item.url)}
                  >
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive ? "bg-sidebar" : ""
                      }
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <SidebarMenuButton disabled>
                          <item.icon />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Para acceder a esta sección, debes verificar tu
                          cuenta.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-0">
        {user?.type === "verified" ||
        user?.type === "unverified" ||
        user?.type === "subscribed" ? (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-semibold h-4">
              Personal
            </SidebarGroupLabel>
            <SidebarMenu>
              {personalLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {user?.type === "verified" || user?.type === "subscribed" ? (
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.startsWith(item.url)}
                    >
                      <NavLink
                        to={item.url}
                        className={({ isActive }) =>
                          isActive ? "bg-sidebar" : ""
                        }
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <SidebarMenuButton disabled>
                            <item.icon />
                            <span>{item.title}</span>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Para acceder a esta sección, debes verificar tu
                            cuenta.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem key="profile">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton className="h-auto p-1 group mb-1 flex items-center gap-2 w-full group/profile">
                      <User className="size-4"/>
                      <div className="grid items-start justify-center text-left group-data-[collapsible=icon]:hidden">
                        <div className="text-xs font-medium truncate">
                          {user?.details?.fullName || "Usuario"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate w-full">
                          {user?.details?.email || ""}
                        </div>
                      </div>
                      <ChevronDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden transition-transform duration-200 group-hover/profile:rotate-180 group-data-[state=open]/profile:rotate-180" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    side="top" 
                    align="end"
                    className="w-50 font-mono"
                  >
                    <DropdownMenuItem asChild>
                      <Link to="/perfil" className="flex items-center w-full gap-2 focus:bg-sidebar focus:text-foreground text-foreground">
                        <User className="size-4" />
                        Perfil
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/facturacion" className="flex items-center w-full gap-2 focus:bg-sidebar focus:text-foreground text-foreground">
                        <Receipt className="size-4" />
                        Facturación
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      variant="destructive"
                      className="flex gap-2"
                      onClick={async () => {
                        await logout.mutateAsync();
                      }}
                    >
                      <LogOut className="size-4" />
                      Cerrar sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        ) : (
          <SidebarGroup> 
            <SidebarGroupLabel className="text-xs font-semibold h-4">
              Autenticación
            </SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem key="register">
                <SidebarMenuButton asChild>
                  <Link to="/registracion">
                    <User /> Registrate
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem key="login">
                <SidebarMenuButton asChild>
                  <Link to="/inicia-sesion">
                    <LogIn /> Iniciar sesión
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarFooter>
      <SidebarRail />
    </SidebarRoot>
  );
};
